'use client'

import { Fragment } from 'react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { ScrollRow } from '@/components/ui/scroll-row'

import { Breadcrumbs } from '@/components/breadcrumbs'
import { EASE, HAIRLINE, useReveal } from '@/components/page-kit'
import { HRMS, HRMS_MODULES } from './data'

/* HRMS's structural device is a *sequence*, not a grid: the six modules are an
   ordered employee lifecycle (Recruitment → Onboarding → Attendance →
   Performance → Compliance → Analytics), so the page is built on a track that
   runs in one direction. That is the deliberate contrast with the UDC page,
   whose six modules are parallel and hang off a shared bus.

   Every string below comes from products.ts — see ./data.ts. The one addition
   is in the h1: the authored title spells the category out in full and never
   says "HRMS", which is the word this page is searched by, and it names no
   audience. Acronym and audience are appended; both are already the page's own
   metadata, so nothing new is claimed. */

export function HrmsHero() {
  const track = useReveal<HTMLDivElement>(0.25)

  return (
    <section
      className={cn(
        'relative overflow-hidden hero-adaptive-bg',
        // The site header is transparent until scrolled, so the hero canvas has
        // to start at the true top of the viewport.
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: 'var(--circuit-bg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
          maskImage:
            'radial-gradient(ellipse 110% 68% at 50% 26%, #000 18%, rgba(0,0,0,0.45) 60%, transparent 94%)',
        }}
      />

      <div className="container relative z-10 mx-auto">
        <div className="flex min-h-[80svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+4rem)] sm:pb-20">
          <Breadcrumbs
            items={[
              { name: 'Home', href: '/' },
              { name: 'Products', href: '/products' },
              { name: HRMS.title, href: `/products/${HRMS.slug}` },
            ]}
          />

          <h1
            className={cn(
              'font-heading mt-6 max-w-4xl font-normal tracking-tight text-foreground',
              'text-[30px]/[1.14] sm:text-[42px]/[1.14] md:text-5xl/[1.12] lg:text-[58px]/[1.1]'
            )}
          >
            <ChromaText>{`${HRMS.title} (${HRMS.shortTitle}) for schools and colleges`}</ChromaText>
          </h1>

          {/* The tagline, exactly as authored. */}
          <p className="mt-5 max-w-2xl text-lg font-medium leading-snug text-emerald-600 sm:text-xl md:text-2xl dark:text-primary-active">
            {HRMS.tagline}
          </p>

          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#2B3A46] sm:text-lg dark:text-[#DEE9EE]/90">
            {HRMS.description}
          </p>

          <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Button asChild size="xl">
              <a href="/schedule-meeting">Request a demo</a>
            </Button>

            <Button variant="outline" asChild size="xl">
              <a href="#included">See what's included</a>
            </Button>
          </div>
        </div>
      </div>

      {/* ── The track ──────────────────────────────────────────────────────
          The lifecycle stated once, in order, as a single directional line.
          Scrolls horizontally on phones rather than wrapping into a grid —
          wrapping would destroy the one thing it is saying, which is sequence.

          The rule runs full-bleed but the steps sit in the container, so the
          first one lines up with the headline above it. Without that the track
          drifts ~250px left of the content on a 1920 viewport, where the
          container caps at 1400px. */}
      <div ref={track.ref} className={cn('relative z-10 border-y', HAIRLINE)}>
        <div className="container mx-auto">
          <ScrollRow
            aria-label="Employee lifecycle"
            className="flex items-center gap-0 py-4 lg:justify-between lg:overflow-visible"
          >
            {HRMS_MODULES.map((m, i) => (
              <Fragment key={m.name}>
                <div
                  className="flex shrink-0 items-baseline gap-2"
                  style={{
                    opacity: track.shown ? 1 : 0,
                    transform: track.shown ? 'none' : 'translateY(8px)',
                    transition: `opacity 460ms ${EASE} ${i * 80}ms, transform 460ms ${EASE} ${i * 80}ms`,
                  }}
                >
                  <span className="text-[10px] font-semibold tabular-nums text-muted-foreground/60">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="font-heading whitespace-nowrap text-sm font-normal text-foreground sm:text-base">
                    {m.name}
                  </span>
                </div>

                {i < HRMS_MODULES.length - 1 && (
                  // Fixed-width while the row scrolls; from lg the connectors
                  // are the flexible element, so the six steps distribute
                  // across the container instead of bunching at one end.
                  <span
                    aria-hidden
                    className="mx-4 h-px w-8 shrink-0 origin-left bg-border sm:mx-6 sm:w-12 lg:w-auto lg:min-w-[24px] lg:flex-1"
                    style={{
                      transform: track.shown ? 'scaleX(1)' : 'scaleX(0)',
                      transition: `transform 400ms ${EASE} ${140 + i * 80}ms`,
                    }}
                  />
                )}
              </Fragment>
            ))}
          </ScrollRow>
        </div>
      </div>
    </section>
  )
}
